import React, { useState } from 'react';
import { Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import imageCompression from 'browser-image-compression';
import { api } from '@/lib/api.ts';

const RegisterReturnShippingModal = ({ isOpen, onClose, orderId, email, onSuccess }) => {
    const [tracking, setTracking] = useState('');
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onloadend = () => setPreview(reader.result);
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!tracking || !file) return toast.error('Debes subir la foto de la guía y el número de tracking');

        setIsSubmitting(true);
        try {
            const compressedFile = await imageCompression(file, {
                maxSizeMB: 0.5,
                maxWidthOrHeight: 2000,
                useWebWorker: true,
                quality: 0.9,
                fileType: 'image/jpeg'
            });

            const finalFile = new File([compressedFile], file.name, { type: compressedFile.type });
            const contentType = finalFile.type || 'application/octet-stream';
            
            const uploadRes = await api.post(`/api/orders/${orderId}/return-shipping-url`, {
                fileName: finalFile.name,
                contentType: contentType
            });
            
            const response = await fetch(uploadRes.data.uploadURL, {
                method: 'PUT',
                body: finalFile,
                headers: { 'Content-Type': contentType }
            });

            if (!response.ok) throw new Error('Falló subida');

            await api.post(`/api/orders/${orderId}/register-return-shipping`, {
                email,
                receipt_path: uploadRes.data.filePath,
                tracking
            });

            toast.success('Envío registrado correctamente');
            onSuccess();
            onClose();
        } catch (error) {
            console.error(error);
            toast.error('Error al registrar el envío');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-md animate-in fade-in">
            <div className="bg-[#0F0F0F] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
                <h3 className="text-lg font-bold text-white mb-1">Registrar Envío</h3>
                <p className="text-xs text-white/50 mb-4">Sube la foto de la guía que te dieron en la agencia.</p>
                
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Foto de la Guía *</label>
                        
                        {preview ? (
                            <div className="relative rounded-xl overflow-hidden border border-green-500/30 group">
                                <img src={preview} alt="Vista previa" className="w-full h-48 object-cover" />
                                <button
                                    type="button"
                                    onClick={() => { setFile(null); setPreview(null); }}
                                    className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                                    <p className="text-xs text-white font-bold truncate">{file.name}</p>
                                </div>
                            </div>
                        ) : (
                            <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:bg-white/5 transition-colors">
                                <div className="flex flex-col items-center gap-2 text-white/30">
                                    <Upload className="h-6 w-6" />
                                    <span className="text-[10px] uppercase font-bold">Subir Foto</span>
                                </div>
                                <input type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
                            </label>
                        )}
                    </div>

                    <div>
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40 mb-1">Número de Guía / Tracking *</label>
                        <input 
                            type="text" 
                            value={tracking} 
                            onChange={e => setTracking(e.target.value)}
                            className="w-full bg-black/50 border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-indigo-500"
                            placeholder="Ej: 12345678"
                        />
                    </div>

                    <div className="flex gap-3 pt-2">
                        <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 text-xs font-bold uppercase tracking-widest hover:bg-white/5">Cancelar</button>
                        <button type="submit" disabled={isSubmitting} className="flex-1 py-3 rounded-xl bg-indigo-600 text-white text-xs font-bold uppercase tracking-widest hover:bg-indigo-500 disabled:opacity-50">
                            {isSubmitting ? 'Registrando...' : 'Confirmar Envío'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RegisterReturnShippingModal;